import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { env, type ServiceName } from '@shared/config/env';
import type { ApiErrorResponse, ApiResponse } from '@shared/types/api';
import type { AuthSession } from '@shared/types/auth';
import { coordinateRefresh } from '@shared/auth/session-coordination';
import { ApiError } from './api-error';
import { IDENTITY } from './endpoints/identity';

/**
 * Per-service HTTP clients.
 *
 * The backend is six services, each on its own origin, each with the same
 * response envelope and the same authentication (an identity-service RS256
 * access token sent as a Bearer header). One client per service makes the
 * owner of every call explicit at the call site — `peopleApi.get(...)` cannot
 * silently reach HRMS — and lets the contract validator check each path
 * against that service's route inventory.
 *
 * ## Session model (identity-service contract)
 *
 * - The access token (15 min, claims `sub` + `sid`) lives in memory only.
 * - The refresh token lives in an HttpOnly, signed cookie named
 *   `refresh_token_<sessionId>` set by identity-service; JavaScript never sees
 *   it. Requests to identity therefore use `withCredentials`.
 * - `POST /auth/refresh` rotates the session. With several sessions in one
 *   browser it needs `X-Session-Id` to know which cookie to read; without the
 *   header it refuses when more than one session cookie is present.
 * - Reusing an old refresh token revokes the session (replay detection), so
 *   concurrent 401s must share ONE refresh — see `refreshAccessToken`.
 */

/** Request options this client understands beyond Axios's own. */
export interface ApiRequestConfig extends AxiosRequestConfig {
  /**
   * Suppress the global "you do not have permission" toast for THIS request.
   *
   * Right for a speculative read — a supplementary panel that renders nothing
   * when the data is not visible. The backend layers row-level scope on top of
   * the permission key, so only the call site knows a refusal is an answer.
   * Never set on a user-initiated action.
   */
  silentForbidden?: boolean;
}

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  silentForbidden?: boolean;
  /** Session that was live in this tab when the request was sent. */
  _sessionId?: string | null;
}

/**
 * Names the session a refresh or logout acts on. Sent by identity-service's
 * `SESSION_ID_HEADER` (`x-session-id`). The session id is not a secret: it is
 * already in the access token's `sid` claim; the secret is the cookie value.
 */
export const SESSION_HEADER = 'X-Session-Id';

// ---------------------------------------------------------------------------
// Redaction — nothing sensitive reaches the dev console.
// ---------------------------------------------------------------------------

/** Lower-case: the lookup compares `key.toLowerCase()`. */
const sensitiveKeys = new Set([
  'authorization',
  'password',
  'newpassword',
  'currentpassword',
  'temporarypassword',
  'temporary_password',
  'refreshtoken',
  'accesstoken',
  'token',
  'secret',
  'account_number',
  'accountnumber',
  'downloadurl',
  'download_url',
  'url',
]);

function redact(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(redact);
  if (value && typeof value === 'object' && !(value instanceof FormData)) {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, entry]) => [
        key,
        sensitiveKeys.has(key.toLowerCase()) ? '[REDACTED]' : redact(entry),
      ]),
    );
  }
  return value;
}

function parseRequestData(data: unknown): unknown {
  if (typeof data !== 'string') return redact(data);
  try {
    return redact(JSON.parse(data));
  } catch {
    return data;
  }
}

function logApi(event: string, details: Record<string, unknown>): void {
  if (env.isDevelopment) console.debug(`[API] ${event}`, details);
}

function requestDetails(config: InternalAxiosRequestConfig): Record<string, unknown> {
  return {
    service: config.baseURL,
    method: config.method?.toUpperCase(),
    url: config.url,
    params: redact(config.params),
    body: parseRequestData(config.data),
  };
}

// ---------------------------------------------------------------------------
// Auth bridge — set by AuthProvider at startup (avoids a circular import).
// ---------------------------------------------------------------------------

export interface AuthBridge {
  getAccessToken: () => string | null;
  /** Session id of the signed-in account, used to scope the refresh. */
  getActiveSessionId?: () => string | null;
  onSessionRefreshed: (session: AuthSession) => void;
  /**
   * identity-service refused the refresh: the session is expired or revoked.
   * Reported once per refused refresh, however many requests were waiting on
   * it. Never called when the refresh merely could not be answered (network,
   * timeout, 5xx, 429): the session may well still be valid then.
   */
  onSessionExpired: () => void;
  /**
   * Authenticated but not permitted (permission, application access or
   * company scope). Never triggers refresh or logout.
   */
  onForbidden?: (error: ApiError) => void;
  /** 429 from any service. */
  onRateLimited?: (error: ApiError) => void;
}

let authBridge: AuthBridge | null = null;

export function registerAuthBridge(bridge: AuthBridge): void {
  authBridge = bridge;
}

// ---------------------------------------------------------------------------
// Refresh coordination — collapse concurrent refreshes into one promise.
// ---------------------------------------------------------------------------

/**
 * Bare identity client for the refresh call only, so the refresh request is
 * never intercepted (which would risk an infinite 401 loop).
 */
const refreshClient: AxiosInstance = axios.create({
  baseURL: env.services.identity,
  timeout: env.apiTimeout,
  withCredentials: true,
  headers: { Accept: 'application/json' },
});

const refreshes = new Map<string, Promise<AuthSession>>();

/**
 * Refusals already reported to the bridge. Every request waiting on one
 * refresh receives the SAME rejection; reporting it once keeps a burst of
 * 401s from ending the session once per request — each report used to
 * forget the saved account that had just taken over.
 */
const reportedRefusals = new WeakSet<object>();

/** identity-service answered the refresh, and said no. */
const isRefusedRefresh = (error: unknown): error is ApiError =>
  error instanceof ApiError && (error.status === 401 || error.status === 403);

/**
 * Rotate a session once, however many requests (and tabs) are waiting on it.
 * A second, parallel refresh would present an already-rotated token, which
 * identity-service treats as replay and answers by revoking the session —
 * so calls are collapsed per session in this tab, and serialized across this
 * application's tabs by `coordinateRefresh`.
 */
export function refreshAccessToken(
  sessionId?: string | null,
  rejectedToken?: string | null,
): Promise<AuthSession> {
  const sid = sessionId === undefined ? (authBridge?.getActiveSessionId?.() ?? null) : sessionId;
  const key = sid ?? 'default';
  const inFlight = refreshes.get(key);
  if (inFlight) return inFlight;
  const promise = coordinateRefresh(
    sid,
    () =>
      refreshClient
        .post<ApiResponse<AuthSession>>(IDENTITY.auth.refresh, undefined, {
          headers: sid ? { [SESSION_HEADER]: sid } : undefined,
        })
        .then((response) => response.data.data),
    rejectedToken,
  )
    .catch((error: unknown) => {
      throw axios.isAxiosError(error)
        ? ApiError.fromAxiosError(error as AxiosError<ApiErrorResponse>)
        : error;
    })
    .finally(() => {
      refreshes.delete(key);
    });
  refreshes.set(key, promise);
  return promise;
}

/**
 * Resolves once no refresh this tab started is still in flight, whatever its
 * outcome. Awaited before the tab leaves for another application: navigating
 * away mid-rotation aborts the request after identity-service may already
 * have rotated the token, and the next application would then present the
 * rotated-out cookie — which identity answers as replay, by revoking the
 * session.
 */
export async function settleRefreshes(): Promise<void> {
  await Promise.allSettled([...refreshes.values()]);
}

/**
 * A download request (`responseType: 'blob'`) receives its ERROR body as a
 * Blob too. Read it back into the JSON envelope so the backend's message —
 * "Attendance export is not implemented yet", a validation failure — reaches
 * the user instead of a generic failure.
 */
async function parseBlobErrorBody(error: AxiosError<ApiErrorResponse>): Promise<void> {
  const response = error.response as { data: unknown } | undefined;
  if (!response || typeof Blob === 'undefined' || !(response.data instanceof Blob)) return;
  try {
    const text = await response.data.text();
    response.data = JSON.parse(text) as ApiErrorResponse;
  } catch {
    response.data = undefined;
  }
}

// ---------------------------------------------------------------------------
// Client factory
// ---------------------------------------------------------------------------

function createServiceClient(service: ServiceName): AxiosInstance {
  const client = axios.create({
    baseURL: env.services[service],
    timeout: env.apiTimeout,
    // Only identity reads cookies; sending them elsewhere is harmless (the
    // other services do not parse cookies) and keeps one CORS posture.
    withCredentials: service === 'identity',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });

  client.interceptors.request.use((config) => {
    const token = authBridge?.getAccessToken();
    if (token) config.headers.set('Authorization', `Bearer ${token}`);
    (config as RetriableConfig)._sessionId = authBridge?.getActiveSessionId?.() ?? null;
    // Let the browser set the multipart boundary.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      config.headers.delete('Content-Type');
    }
    logApi('request', requestDetails(config));
    return config;
  });

  client.interceptors.response.use(
    (response: AxiosResponse) => {
      logApi('response', {
        service,
        method: response.config.method?.toUpperCase(),
        url: response.config.url,
        status: response.status,
      });
      return response;
    },
    async (error: AxiosError<ApiErrorResponse>) => {
      await parseBlobErrorBody(error);
      const original = error.config as RetriableConfig | undefined;
      const status = error.response?.status;
      const isAuthCall =
        service === 'identity' &&
        (original?.url === IDENTITY.auth.refresh || original?.url === IDENTITY.auth.login);

      if (env.isDevelopment && error.code !== 'ERR_CANCELED') {
        console.debug('[API] request failed', {
          ...(original ? requestDetails(original) : {}),
          status,
          message: error.response?.data?.message ?? error.message,
        });
      }

      if (status === 403) {
        const forbidden = ApiError.fromAxiosError(error);
        if (!original?.silentForbidden) authBridge?.onForbidden?.(forbidden);
        return Promise.reject(forbidden);
      }

      if (status === 429) {
        const limited = ApiError.fromAxiosError(error);
        authBridge?.onRateLimited?.(limited);
        return Promise.reject(limited);
      }

      if (status === 401 && original && !original._retry && !isAuthCall && authBridge) {
        original._retry = true;
        const rejectedToken = String(original.headers.get('Authorization') ?? '').replace(/^Bearer /, '');
        // A 401 that arrives after the session which sent the request stopped
        // being the live one in this tab — it already ended, or another account
        // took over meanwhile — is not this session's to refresh or to end:
        // doing either would act on (and could forget) the wrong account.
        const sentBy = original._sessionId ?? null;
        const sessionEnded = Boolean(rejectedToken) && !authBridge.getAccessToken();
        const sessionChanged = sentBy !== null && sentBy !== (authBridge.getActiveSessionId?.() ?? null);
        if (sessionEnded || sessionChanged) {
          return Promise.reject(ApiError.fromAxiosError(error));
        }
        try {
          const session = await refreshAccessToken(undefined, rejectedToken || null);
          authBridge.onSessionRefreshed(session);
          original.headers.set('Authorization', `Bearer ${session.accessToken}`);
          return client(original);
        } catch (refreshError) {
          if (isRefusedRefresh(refreshError)) {
            if (!reportedRefusals.has(refreshError)) {
              reportedRefusals.add(refreshError);
              authBridge.onSessionExpired();
            }
            return Promise.reject(ApiError.fromAxiosError(error));
          }
          // The refresh could not be answered (identity unreachable, timed out,
          // 5xx, 429): the session is left as it is — its refresh cookie may
          // still be valid — and this request fails with that reason, so trying
          // again later just works. Never with this request's own 401, which the
          // UI leaves unannounced because a 401 normally ends the session.
          const reason =
            refreshError instanceof ApiError
              ? refreshError
              : new ApiError('Your session could not be renewed. Please try again.', 0);
          // The refresh client has no interceptors: announce its 429 here, as any other 429 is.
          if (reason.isRateLimited) authBridge.onRateLimited?.(reason);
          return Promise.reject(reason);
        }
      }

      return Promise.reject(ApiError.fromAxiosError(error));
    },
  );

  return client;
}

/** identity-service :3001 — authentication, users, roles, permissions, provisioning. */
export const identityApi = createServiceClient('identity');
/** people-service :3002 — employee master and organization structure (HRMS application). */
export const peopleApi = createServiceClient('people');
/** hrms-backend :3003 — HR and operations (HRMS application). */
export const hrmsApi = createServiceClient('hrms');
/** finance-service :3004 — company master, Finance, assets, clients, invoices, access administration. */
export const financeApi = createServiceClient('finance');
/** portal-service :3006 — Employee Portal self-service (EMPLOYEE_PORTAL application). */
export const portalApi = createServiceClient('portal');
/** audit-service :3005 — central audit log (read-only). */
export const auditApi = createServiceClient('audit');

export const serviceClients = {
  identity: identityApi,
  people: peopleApi,
  hrms: hrmsApi,
  finance: financeApi,
  portal: portalApi,
  audit: auditApi,
} as const satisfies Record<ServiceName, AxiosInstance>;
